import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { GOOGLE_SERVICE_NAMES, useGoogleServiceConnection } from '@/hooks/useGoogleServiceConnection';
import { useToast } from '@/hooks/useToast';
import { userProfileQueryOptions } from '@/lib/query-options';
import type { GoogleService, GoogleServiceResource, GoogleServiceStatus } from '@/lib/types';

export const GOOGLE_SERVICE_LOGOS: Record<GoogleService, string> = {
  google_analytics: '/google-analytics.svg',
  tag_manager: '/google-tag-manager.svg',
};

const RESOURCE_NOUNS: Record<GoogleService, { title: string; plural: string }> = {
  google_analytics: { title: 'Properties', plural: 'properties' },
  tag_manager: { title: 'Containers', plural: 'containers' },
};

const SEARCH_THRESHOLD = 5;

const resourceId = (resource: GoogleServiceResource) =>
  resource.public_id ?? resource.resource_name.replace(/^properties\//, '');

const ResourceRow = ({ service, resource }: { service: GoogleService; resource: GoogleServiceResource }) => (
  <div className="w-full flex items-center gap-3 p-3 rounded-lg bg-white">
    <div className="flex-shrink-0 w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center">
      <img src={GOOGLE_SERVICE_LOGOS[service]} alt={GOOGLE_SERVICE_NAMES[service]} className="w-4 h-4" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-900 truncate">{resource.display_name}</p>
      <p className="text-xs text-gray-500 truncate">
        {resourceId(resource)}
        {resource.parent_display_name ? ` · ${resource.parent_display_name}` : ''}
      </p>
    </div>
    {resource.linked_to_google_ads === true && (
      <div className="flex items-center gap-1 text-xs font-medium text-[#047857]">
        <CheckCircle className="h-4 w-4" />
        Linked to Google Ads
      </div>
    )}
  </div>
);

interface GoogleServiceManageModalProps {
  service: GoogleService;
  status: GoogleServiceStatus;
  isOpen: boolean;
  onClose: () => void;
  /** An owner or admin, who may replace the connection with a fresh Google grant. */
  canRefresh: boolean;
}

/**
 * Manage for a Google Analytics or Tag Manager connection (#6879), on the Google Ads account picker's layout: what
 * the connected Google account reaches, a search past five rows, and a refresh that runs Google's popup again so
 * newly granted properties or containers, or another Google account, come through. Refresh is hidden while
 * impersonating, as on Google Ads: the popup would connect the admin's own Google account to the client.
 */
export const GoogleServiceManageModal = ({ service, status, isOpen, onClose, canRefresh }: GoogleServiceManageModalProps) => {
  const { toast } = useToast();
  const name = GOOGLE_SERVICE_NAMES[service];
  const nouns = RESOURCE_NOUNS[service];
  const { data: userProfile } = useQuery(userProfileQueryOptions());
  const showRefresh = canRefresh && !userProfile?.impersonating_email;
  const connection = useGoogleServiceConnection(service, {
    onConnected: () =>
      toast({ title: 'Connection Refreshed', description: `Checking what the Google account can reach in ${name}.` }),
  });
  const [searchQuery, setSearchQuery] = useState('');

  const resources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return status.resources;
    return status.resources.filter((resource) =>
      [resource.display_name, resourceId(resource), resource.parent_display_name ?? ''].some((value) =>
        value.toLowerCase().includes(query),
      ),
    );
  }, [status.resources, searchQuery]);

  const emptyState = () => {
    if (status.state === 'checking') {
      return (
        <div className="text-center py-8 text-muted-foreground space-y-2">
          <p className="text-lg font-medium">Checking {name}</p>
          <p className="text-sm">The {nouns.plural} {status.google_email} can reach are being listed.</p>
        </div>
      );
    }
    if (status.resources.length > 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>No {nouns.plural} found matching "{searchQuery}"</p>
        </div>
      );
    }
    return (
      <div className="text-center py-8 space-y-4">
        <div className="text-muted-foreground space-y-2">
          <p className="text-lg font-medium">No {name} {nouns.title} Found</p>
          <p className="text-sm">
            No {name} {nouns.plural} were found for {status.google_email}.
          </p>
          {showRefresh && (
            <p className="text-sm">If there are {name} {nouns.plural}, try refreshing the connection.</p>
          )}
        </div>
        {showRefresh && (
          <Button onClick={connection.connect} disabled={connection.isAuthorizing} className="mt-4">
            <RefreshCw className={`mr-2 h-4 w-4 ${connection.isAuthorizing ? 'animate-spin' : ''}`} />
            {connection.isAuthorizing ? 'Refreshing...' : `Refresh ${name} Connection`}
          </Button>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg shadow-lg">
        <DialogHeader className="p-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
                <img src={GOOGLE_SERVICE_LOGOS[service]} alt={name} className="w-6 h-6" />
                {name} {nouns.title}
              </DialogTitle>
              <DialogDescription>
                The {name} {nouns.plural} {status.google_email} can reach for this organization.
              </DialogDescription>
            </div>
            {showRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={connection.connect}
                disabled={connection.isAuthorizing}
                title={`Refresh ${nouns.plural} from Google`}
                aria-label={`Refresh ${name} ${nouns.plural}`}
                className="shrink-0 mt-1 mr-8"
              >
                <RefreshCw className={`h-4 w-4 ${connection.isAuthorizing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </DialogHeader>
        <Separator className="my-2" />
        <Card className="border-none shadow-none">
          <CardContent className="p-2 space-y-6 rounded-xl">
            {status.resources.length > SEARCH_THRESHOLD && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={`Search ${nouns.plural}...`}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="pl-10"
                />
              </div>
            )}
            <div className="bg-slate-100 rounded-xl p-2 max-h-[60vh] overflow-y-auto overflow-x-clip space-y-1">
              {resources.length > 0
                ? resources.map((resource) => (
                    <ResourceRow key={resource.resource_name} service={service} resource={resource} />
                  ))
                : emptyState()}
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};
